// Legal pages — EULA, Privacy, Refund. Original text, styled as
// "signal protocol documents": mono-numbered sections over hairline rules.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DocSection, MetaChip, PageShell } from './shell';
import { SUPPORT_EMAIL } from '../sections/footer';
import { getConsent, type Consent } from '../consent';
import { phSetConsent } from '../posthog';

const Mail = () => <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;

const LAST_UPDATED = 'June 2026';
// Privacy policy moves on its own clock — the analytics/cookies section is new (2026-08).
const PRIVACY_UPDATED = 'August 2026';

// Inline cookie-preference control — GDPR wants withdrawing consent to be as
// easy as giving it, so the choice lives right inside the policy that
// describes it (the banner itself only ever shows once).
function CookiePrefs() {
  const [choice, setChoice] = useState<Consent | null>(getConsent);
  const pick = (v: Consent) => { phSetConsent(v); setChoice(v); };
  // one SEGMENTED control (not two competing buttons): the filled+checked
  // segment states the current choice; verbs match the banner verbatim
  const seg = (v: Consent, label: string, first: boolean) => {
    const on = choice === v;
    return (
      <button
        onClick={() => pick(v)}
        className="crw-linkbtn"
        aria-pressed={on}
        style={{
          fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.04em',
          padding: '8px 14px', borderRadius: 0,
          borderLeft: first ? 'none' : '1px solid var(--line-2)',
          color: on ? 'var(--signal-300)' : 'var(--fg-3)',
          background: on ? 'var(--signal-dim)' : 'transparent',
        }}
      >
        {on ? '✓ ' : ''}{label}
      </button>
    );
  };
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
      marginTop: 16, marginBottom: 28, padding: '13px 16px', borderRadius: 12,
      border: '1px solid var(--line-2)', background: 'var(--surface-card)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--fg-2)', marginRight: 'auto' }}>
        Your cookie choice:{' '}
        <b style={{ color: 'var(--fg-1)', fontWeight: 600 }}>
          {choice === 'granted' ? 'accepted' : choice === 'denied' ? 'declined' : 'not made yet'}
        </b>
      </span>
      <div style={{ display: 'flex', border: '1px solid var(--line-2)', borderRadius: 9, overflow: 'hidden' }}>
        {seg('granted', 'Accept', true)}
        {seg('denied', 'Decline', false)}
      </div>
    </div>
  );
}

export function EulaPage() {
  return (
    <PageShell
      kicker="Legal · EULA"
      title="End-User License Agreement"
      sub="The agreement between you and NDX Group that covers your use of the ClipRadar desktop app and the ClipRadar panel for Premiere Pro."
      meta={<><MetaChip>Last updated · {LAST_UPDATED}</MetaChip><MetaChip>NDX Group · Bulgaria</MetaChip><MetaChip>Beta software</MetaChip></>}
    >
      <DocSection n="00" title="The short version">
        <p>
          You buy a license, you get to use ClipRadar on one machine at a time. Your footage is yours and never
          leaves your computer. Don't share your license key, don't resell or reverse-engineer the software.
          ClipRadar is in beta — it will improve fast, and occasionally it will misbehave. The full terms below
          are what legally counts, but they are written to match this summary.
        </p>
      </DocSection>

      <DocSection n="01" title="The agreement">
        <p>
          This End-User License Agreement ("Agreement") is between you and <strong>NDX Group</strong>, a company
          incorporated in Bulgaria ("we", "us"). It governs your installation and use of the ClipRadar desktop
          application, the ClipRadar extension panel for Adobe Premiere Pro, and related updates (together, the
          "Software"). By installing or using the Software you accept this Agreement. If you do not accept it,
          do not install or use the Software.
        </p>
      </DocSection>

      <DocSection n="02" title="The license">
        <p>
          Subject to this Agreement and payment of the applicable fee, we grant you a personal, non-exclusive,
          non-transferable license to install and use the Software for your own projects — personal or commercial.
        </p>
        <p>
          Each license key activates the Software on <strong>one machine at a time</strong>. You can deactivate a
          machine and move your license to another one from inside the app. We may use technical measures
          (including a hashed machine identifier) to enforce activation limits.
        </p>
      </DocSection>

      <DocSection n="03" title="Restrictions">
        <ul>
          <li>Don't share, publish, sell, or rent your license key, and don't sublicense the Software.</li>
          <li>Don't reverse-engineer, decompile, or modify the Software, except where applicable law expressly permits it despite this limitation.</li>
          <li>Don't bypass or attempt to bypass license enforcement.</li>
          <li>Don't use the Software in violation of applicable law.</li>
        </ul>
      </DocSection>

      <DocSection n="04" title="Your content">
        <p>
          Your footage, audio, transcripts, and projects are yours. The Software processes your media
          <strong> locally on your machine</strong>; we claim no rights over your content, and the Software does
          not upload it to us. See the <Link to="/legal/privacy">Privacy Policy</Link> for the small set of data
          we do process (account, licensing, payments).
        </p>
      </DocSection>

      <DocSection n="05" title="Open-source notices">
        <p>
          The Software bundles open-source components under their respective licenses. The applicable notices and
          license texts ship with the Software distribution. Open-source components remain governed by their own
          licenses where those terms conflict with this Agreement.
        </p>
      </DocSection>

      <DocSection n="06" title="Intellectual property">
        <p>
          The Software, including its code, design, and the ClipRadar name and marks, is owned by NDX Group and
          protected by intellectual-property law. This Agreement gives you a right to use the Software; it does
          not transfer any ownership to you.
        </p>
      </DocSection>

      <DocSection n="07" title="Beta software">
        <p>
          ClipRadar is currently distributed as <strong>beta software</strong>. Features may change, break, or be
          removed; indexes may need to be rebuilt between versions. Keep backups of your projects and media. We
          make no commitment that the beta is fit for any particular production workflow.
        </p>
      </DocSection>

      <DocSection n="08" title="Updates & termination">
        <p>
          We may release updates that change or fix the Software, and certain updates may be required for license
          validation to keep working. This Agreement remains in effect until terminated. It terminates
          automatically if you materially breach it — including key sharing or chargeback abuse — in which case
          your license key may be revoked. Sections 04, 06, 09, and 10 survive termination.
        </p>
      </DocSection>

      <DocSection n="09" title="Disclaimers & limitation of liability">
        <p>
          The Software is provided <strong>"as is"</strong>, without warranties of any kind, express or implied,
          including fitness for a particular purpose. To the maximum extent permitted by law, NDX Group is not
          liable for indirect, incidental, or consequential damages (including lost profits, lost footage, or lost
          edit time), and our total liability under this Agreement is limited to the amount you paid for the
          Software in the twelve months before the claim. Nothing in this Agreement excludes liability that
          cannot be excluded under applicable law, and nothing limits the statutory rights you hold as a consumer.
        </p>
      </DocSection>

      <DocSection n="10" title="Governing law & contact">
        <p>
          This Agreement is governed by the laws of <strong>Bulgaria</strong>; mandatory consumer-protection rules
          of your country of residence remain unaffected. Questions about this Agreement: <Mail />.
        </p>
      </DocSection>
    </PageShell>
  );
}

export function PrivacyPage() {
  return (
    <PageShell
      kicker="Legal · Privacy"
      title="Privacy Policy"
      sub="The headline: ClipRadar processes your footage entirely on your machine. We never see your media — and this page lists the little we do see."
      meta={<><MetaChip>Last updated · {PRIVACY_UPDATED}</MetaChip><MetaChip>Controller · NDX Group (Bulgaria)</MetaChip><MetaChip>GDPR</MetaChip></>}
    >
      <DocSection n="01" title="Local first, by design">
        <p>
          Everything ClipRadar does to your media — visual indexing, speech transcription, face recognition —
          happens <strong>100% locally on your computer</strong>. Your footage, audio, transcripts, thumbnails,
          and face data are stored on your own disk and are never uploaded to us or anyone else by the app.
          We could not look at your footage even if we wanted to.
        </p>
      </DocSection>

      <DocSection n="02" title="What we do process">
        <ul>
          <li><strong>Email & license data</strong> — your email address, license key, and a hashed machine identifier used for activation (processed through Keygen, our licensing provider).</li>
          <li><strong>Payment data</strong> — handled by Stripe; we receive order details but never see or store your card number.</li>
          <li><strong>Waitlist & email</strong> — if you join the waitlist or write to us, we keep your email address and correspondence (delivered through Resend).</li>
          <li><strong>Website hosting</strong> — the site is served by Cloudflare, which processes standard request logs to deliver it.</li>
        </ul>
      </DocSection>

      <DocSection n="03" title="Analytics & cookies">
        <p>
          We measure how the website and the app are used with <strong>PostHog</strong>, hosted in the EU.
          By default this is <strong>cookieless and anonymous</strong> — no identifier is stored on your
          device, and each visit looks like a new one. If you accept the cookie banner, we set an
          analytics cookie so return visits are recognized; if you start a trial, sign in, or buy,
          your email is attached to that history so we can see our own sales funnel end to end.
          The desktop app sends anonymous usage events (feature usage, version, error signals) only
          if you opt in to telemetry during onboarding — never your media or transcripts.
        </p>
        <ul>
          <li>No advertising trackers, no cross-site tracking, no selling or renting of data — ever.</li>
          <li>Declining (or ignoring) the banner keeps you fully anonymous and costs you nothing.</li>
        </ul>
        <CookiePrefs />
      </DocSection>

      <DocSection n="04" title="What we don't do">
        <ul>
          <li>No upload of your media, transcripts, or face data — ever.</li>
          <li>No selling or renting of personal data. No advertising profiles.</li>
        </ul>
      </DocSection>

      <DocSection n="05" title="Processors we rely on">
        <p>
          Keygen (licensing), Stripe (payments), Resend (email), Cloudflare (website & DNS), PostHog (analytics,
          EU-hosted), and Discord (our optional community — governed by Discord's own terms when you join).
          Each receives only what it needs for its function.
        </p>
      </DocSection>

      <DocSection n="06" title="Legal bases & retention">
        <p>
          We process the data above to perform our contract with you (licensing, payments, support), and on
          legitimate interest for securing licenses against abuse. We keep licensing and order records for as
          long as your license is active plus the period required by tax and accounting law; waitlist emails
          until you unsubscribe; support threads as long as useful for helping you.
        </p>
      </DocSection>

      <DocSection n="07" title="Your rights">
        <p>
          Under the GDPR you can request access to, correction of, export of, or deletion of your personal data,
          object to processing, and lodge a complaint with a supervisory authority (in Bulgaria: the Commission
          for Personal Data Protection). One email to <Mail /> is enough to exercise any of these.
        </p>
      </DocSection>

      <DocSection n="08" title="Controller & changes">
        <p>
          The data controller is <strong>NDX Group</strong>, incorporated in Bulgaria — reachable at <Mail />.
          If this policy changes materially, the "last updated" stamp above changes with it, and significant
          changes will be announced in the app or by email.
        </p>
      </DocSection>
    </PageShell>
  );
}

export function RefundPage() {
  return (
    <PageShell
      kicker="Legal · Refunds"
      title="Refund Policy"
      sub="Fourteen days, no interrogation. If ClipRadar doesn't work out for you, we'd rather refund you than keep your money and lose your trust."
      meta={<><MetaChip>Last updated · {LAST_UPDATED}</MetaChip><MetaChip>14-day window</MetaChip></>}
    >
      <DocSection n="01" title="The promise">
        <p>
          If ClipRadar isn't working for you, request a refund within <strong>14 days of your purchase or
          renewal</strong> and we'll process it — we'll ask what went wrong only because it helps us fix the
          product, never as a condition.
        </p>
      </DocSection>

      <DocSection n="02" title="How to request one">
        <p>
          Email <Mail /> from (or mentioning) the address you purchased with, or ping us on{' '}
          <a href="https://discord.gg/7aAtq7a2e9" target="_blank" rel="noopener noreferrer">Discord</a>.
          Refunds go back to the original payment method; banks typically take 5–10 business days to show it.
        </p>
      </DocSection>

      <DocSection n="03" title="After 14 days">
        <p>
          Past the window we don't refund the current billing period, but you can cancel anytime to stop all
          future charges — cancellation takes effect at the end of the period you've paid for. If something
          exceptional happened (double charge, billing error, the app never activated), write to us anyway;
          we deal with those case by case, in your favor where we reasonably can.
        </p>
      </DocSection>

      <DocSection n="04" title="Renewals">
        <p>
          Subscriptions renew automatically. Each renewal charge starts its own 14-day refund window, so a
          renewal you didn't mean to keep is covered — just write within two weeks of the charge.
        </p>
      </DocSection>

      <DocSection n="05" title="EU right of withdrawal">
        <p>
          If you are an EU consumer, you have a statutory 14-day right of withdrawal for digital purchases.
          Nothing in this policy limits it — in practice our standard refund promise above already matches or
          exceeds it.
        </p>
      </DocSection>

      <DocSection n="06" title="The fine print">
        <p>
          When a refund is issued, the associated license key is deactivated. Please contact us before opening a
          chargeback — chargebacks cost fees, take weeks longer than we do, and lead to the same outcome.
          Repeated purchase-refund cycles of the same product may be declined.
        </p>
      </DocSection>
    </PageShell>
  );
}
